import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Question, Trash } from '@phosphor-icons/react';
import { Button, Input, Checkbox, Dropdown, Select } from 'app/components';
import { EMAIL_TYPE, FORM_DROPDOWN, OPTION_TYPES, SUB_QUESTION_ALLOWED_TYPES } from '../../constants';
import DraggableList from '../draggable-list';
import SubQuestionBuilder from './subQuestionBuilder';
import { generateUniqueId, removeSubOptions } from '../../utils';

const Option = (props) => {
  const {
    inputProps,
    className,
    value,
    onDelete,
    onChange,
    onAdd,
    index,
    others,
    ...attr
  } = props;
  const ref = useRef(null);

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onAdd();

      return;
    }

    // onChange(index, e?.target?.value);
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex-1">
        <Input
          width="100%"
          value={value}
          onChange={(e) => onChange(index, e?.target?.value)}
          onKeyDown={onKeyDown}
          {...(others && ({ placeholder: 'Others' }))}
          ref={ref}
          {...inputProps}
          {...attr}
          className={`${inputProps?.className ? inputProps.className + ' ' : ''}${attr?.className ? attr.className + ' ' : ''}w-full`}
        />
      </div>
      <Trash
        color="#101010"
        className="cursor-pointer"
        size={20}
        onClick={() => onDelete(index)}
      />
    </div>
  );
};

const Form = (props) => {
  const {
    formConfig,
    errors,
    setErrors,
    onChange,
    allowSubQuestion,
    ticketsConfig,
  } = props;

  const onOptionDelete = (index) => {
    const updatedOptions = [...(formConfig?.options || [])];
    updatedOptions.splice(index, 1);
    onChange('options', updatedOptions);
    onChange('conditions', removeSubOptions(formConfig?.options?.[index]?.id, formConfig));
  };

  const onOptionChange = (index, value) => {
    const updatedOptions = [...(formConfig?.options || [])];
    updatedOptions[index] = { ...updatedOptions?.[index], value };
    onChange('options', updatedOptions);
  };

  const onAddOption = () => {
    const id = generateUniqueId();
    let updatedOptions = formConfig?.options?.filter((opt) => !opt?.others);
    const otherOption = formConfig?.options?.filter((opt) => opt?.others);
    updatedOptions = [...(updatedOptions || []), { value: '', id }, ...(otherOption?.length ? otherOption : [])];
    onChange('options', updatedOptions);

    setTimeout(() => document.getElementById(id)?.focus(), 50);
  };

  const onAddOthers = () => {
    const updatedOptions = [...(formConfig?.options || []), { value: '', others: true }];
    onChange('options', updatedOptions);
  };

  const onAddCondition = () => {
    const updatedOptions = [...(formConfig?.conditions || []), { choice: '', config: { id: generateUniqueId() } }];
    onChange('conditions', updatedOptions);
  };

  const onToggleTicketType = () => {
    if (formConfig?.specificTicketType) onChange('ticketTypes', []);

    onChange('specificTicketType', !formConfig?.specificTicketType);
  };

  const onChangeTickets = (id) => {
    const updatedTickets = [...(formConfig?.ticketTypes || [])];

    if (updatedTickets?.includes(id)) updatedTickets.splice( updatedTickets.indexOf(id),1);
    else updatedTickets.push(id);

    onChange('ticketTypes', updatedTickets);
  };

  return (
  <div className="form-container flex flex-col gap-4 p-4 md:p-0 items-start w-full self-start overflow--hidden">
  <div className="questions-container flex flex-col md:flex-row gap-4 w-full">
        <Input
          width="100%"
          className="w-full md:flex-1"
          label="Question"
          placeholder="Ask a question"
          value={formConfig?.label}
          onChange={(e) => {
            if (errors?.label && e?.target?.value?.trim()) {
              setErrors((prev) => {
                delete prev.label;
                return prev;
              });
            }
            onChange('label', e?.target?.value);
          }}
          {...errors?.label && ({ validationStatus: 'error', helpText: errors.label })}
  />
        <div className="w-full md:w-[220px]">
          <Dropdown
            width="100%"
            label="Type"
            placeholder="Select Option"
            options={FORM_DROPDOWN}
            value={formConfig?.type}
            showSelection={false}
            onChange={(value) => {
              if (errors?.type) {
                setErrors((prev) => {
                  delete prev.type;
                  return prev;
                });
              }
              onChange('type', value);
            }}
            className="w-full"
            {...errors?.type && ({ validationStatus: 'error', helpText: errors.type })}
          />
        </div>
      </div>
      {formConfig?.type === EMAIL_TYPE && (
        <Checkbox
          label="Allow only specific Mail IDs to purchase tickets"
          className="mt-8"
          checked={formConfig?.showSpecific}
          onChange={() => onChange('showSpecific', !formConfig?.showSpecific)}
        />)}
      {(OPTION_TYPES.includes(formConfig?.type) || formConfig?.showSpecific) && (
        <>
          <div className="options-wrapper mt-8">
            <div className="label-16 text-grey-70 pb-2">{formConfig?.type} options</div>
            <DraggableList
              list={formConfig?.options?.filter((item) => !item?.others) || []}
              Component={Option}
              componentProps={{
                type: formConfig?.type,
                onChange: onOptionChange,
                onDelete: onOptionDelete,
                onAdd: onAddOption,
                ...(formConfig?.type === EMAIL_TYPE && ({
                  inputProps: {
                    contentLeft: <>@</>,
                  },
                })),
              }}
              onChange={(data) => onChange('options', data, 're-arrange')}
            />
            {formConfig?.options?.some((item) => item?.others) && (
              <Option
                {...formConfig?.options?.find((item) => item?.others)}
                index={formConfig?.options?.length - 1}
                onDelete={onOptionDelete}
                className="mt-5 ml-6"
              />
            )}
            <div className="flex flex-wrap items-center mt-4 gap-2.5">
              <Button
                className="add-option-button"
                type="tertiary-text"
                iconLeft={<Plus />}
                onClick={onAddOption}
              >
                {formConfig?.type === 'Email' ? 'Add Mail ID' : 'Add option'}
              </Button>
              {(formConfig?.type === 'Multiple choice' && (formConfig?.options || [])?.every((item) => !item?.others)) && (
                <>
                  <span className="label-14 text-black">Or</span>
                  <Button
                    className="add-option-button"
                    type="tertiary-text"
                    onClick={onAddOthers}
                  >
                    Add "Others"
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="horizontal-divider my-8" />
          {(allowSubQuestion && SUB_QUESTION_ALLOWED_TYPES.includes(formConfig?.type)) && (
            <>
              <Checkbox
                // className="mb-8"
                label="Add a conditional sub-question"
                checked={!!formConfig?.conditions?.length}
                onChange={() => {
                  if (formConfig?.conditions?.length) onChange('conditions', null);
                  else onAddCondition();
                }}
              />
              {!!formConfig?.conditions?.length && (
                <SubQuestionBuilder
                  conditions={formConfig?.conditions}
                  options={formConfig?.options}
                  onAddSubQuestion={onAddCondition}
                  onChange={onChange}
                />)}
            </>
          )}
        </>)}
      {allowSubQuestion && (
        <>
          <Checkbox
            label="Show question for specific ticket types"
            className="mt-8"
            checked={formConfig?.specificTicketType}
            onChange={onToggleTicketType}
          />
          {!!formConfig?.specificTicketType && (
            <div className="flex flex-col gap-2 pl-8 pt-2">
              {ticketsConfig?.map((ticket) => (
                <Checkbox
                  label={ticket?.title}
                  checked={formConfig?.ticketTypes?.includes(ticket?.id)}
                  onChange={() => onChangeTickets(ticket?.id)}
                />
              ))}
            </div>)}
          <div className='flex flex-col gap-2 mt-8'>
            <div className='flex items-center gap-[6px]'>
              <div className='label-14 text-black'>Ask this question per</div>
              <Question size={20}
                color="#878787" />
            </div>
            <div className='flex flex-col md:flex-row gap-2'>
              <Select
                className="w-full md:grow"
                label="Transaction"
                selected={!!formConfig?.is_question_per_transaction}
                onClick={() => onChange('is_question_per_transaction', true)}
              />
              <Select
                className="w-full md:grow"
                label="Ticket"
                selected={!formConfig?.is_question_per_transaction}
                onClick={() => onChange('is_question_per_transaction', false)}
              />
            </div>
          </div>
        </>)}
    </div>
  );
};

Form.propTypes = {
  formConfig: PropTypes.shape({}),
  onChange: PropTypes.func,
  allowSubQuestion: PropTypes.bool,
  ticketsConfig: PropTypes.arrayOf(PropTypes.shape()),
};

Form.defaultProps = {
  formConfig: null,
  onChange: () => null,
  allowSubQuestion: true,
  ticketsConfig: [],
};

export default Form;
